import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeRoleStatus,
  createRole,
  deleteRole,
  getRoles,
  setRoleGrants,
  updateRole,
} from '../api/rolesApi'
import type { PagedResponse, Role, RoleStatus, RoleUpsert } from '../types'

const KEY = 'admin-roles'

/** Rol yönetimi sunucu-durumu: liste + CRUD/durum + yetki atama (set). */
export function useRoles() {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => qc.invalidateQueries({ queryKey: [KEY] })

  const applyUpdated = (updated: Role) =>
    qc.setQueryData<PagedResponse<Role>>([KEY, i18n.language], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((r) => (r.id === updated.id ? updated : r)) }
        : prev,
    )

  const list = useQuery({ queryKey: [KEY, i18n.language], queryFn: getRoles })

  // meta.handledCodes: domaine özel iş kodları ekranda ele alınır → merkezi hata toast'ı (019) susar.
  const create = useMutation({
    mutationFn: (body: RoleUpsert) => createRole(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RoleUpsert }) => updateRole(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })
  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RoleStatus }) => changeRoleStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['ROLE_IN_USE'] },
  })
  const remove = useMutation({
    mutationFn: (id: number) => deleteRole(id),
    onSuccess: invalidate,
    meta: { handledCodes: ['ROLE_IN_USE'] },
  })
  const saveGrants = useMutation({
    mutationFn: ({ id, grantIds }: { id: number; grantIds: number[] }) => setRoleGrants(id, grantIds),
    // Rol yetkileri değişince oturumdaki kullanıcının efektif yetkileri (/me) tazelensin → buton/kolon
    // görünürlüğü tam sayfa yenilemeye gerek kalmadan güncellenir (aksi halde /me staleTime:Infinity ile bayat kalır).
    //
    // Yetki listesi de tazelenir (118): Yetki Yönetimi'ndeki "Verildiği Roller" kolonu bu atamayı
    // gösteriyor. Geçersiz kılınmasaydı buradan atama yapıp o ekrana geçen kullanıcı, önbellekteki
    // listede ESKİ rolleri görürdü — kolonun yanıtladığı sorunun ta kendisini yanlış yanıtlardı.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] })
      qc.invalidateQueries({ queryKey: ['admin-grants'] })
    },
  })

  return { list, create, update, changeStatus, remove, saveGrants }
}
